import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { GriddStyleSheetData } from "@/types/document";

import type { RootState } from "./index";

// Uncommitted stylesheet-preview state. Session-only: never persisted, never hydrated
// (TODO §10.3). The editor iframe writes drafts here; the host pushes the effective
// sheet to every embed for live preview. Dirtiness is exactly `draft !== null` — do
// not infer it from key counts (Gridd's abandonChanges* leaves keys set to undefined).
interface StyleDraftState {
  draft: GriddStyleSheetData | null;
}

const initialState: StyleDraftState = { draft: null };

const styleDraftSlice = createSlice({
  name: "styleDraft",
  initialState,
  reducers: {
    // A live edit from the editor iframe (the effective draftSheets[k] ?? sheets[k]).
    draftChanged(state, action: PayloadAction<GriddStyleSheetData>) {
      state.draft = action.payload;
    },
    // Save: the caller promotes the draft into documentSlice.styleSheet, then clears
    // here so the effective sheet falls back to the (now updated) committed one.
    draftCommitted(state) {
      state.draft = null;
    },
    // Cancel: drop the draft; the host re-pushes the committed sheet and embeds revert.
    draftAbandoned(state) {
      state.draft = null;
    },
  },
});

export const { draftChanged, draftCommitted, draftAbandoned } =
  styleDraftSlice.actions;

export default styleDraftSlice.reducer;

// The draft wins while a preview session is active; otherwise the committed sheet.
// This is what the host pushes into each embed (as its committed sheet) for preview.
export const selectEffectiveStyleSheet = (
  state: RootState,
): GriddStyleSheetData => state.styleDraft.draft ?? state.document.styleSheet;
